import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { Flex, Button, Text, Popover, Icon, iconWeight } from '@nula/core-ui';
import { Link } from '@/components/link/link';
import { useNavigateToEntity } from '@/hooks/navigation';
import { useCalendarStore } from '@/stores/use-calendar-store';
import styles from './markdown-renderer.module.css';

const LIST_MAX = 7;

/** Extract plain text from a react-markdown li node for search filtering. */
function liText(child: React.ReactNode): string {
  if (typeof child === 'string') return child;
  if (typeof child === 'object' && child !== null && 'props' in child) {
    const props = (child as React.ReactElement).props as { children?: React.ReactNode };
    return liText(props.children);
  }
  if (Array.isArray(child)) return child.map(liText).join('');
  return '';
}

interface TruncatedListProps {
  Tag: 'ul' | 'ol';
  className: string;
  children: React.ReactNode[];
}

function OverflowContent({ items, Tag, className }: { items: React.ReactNode[]; Tag: 'ul' | 'ol'; className: string }) {
  const [query, setQuery] = useState('');
  const filtered = query
    ? items.filter((c) => liText(c).toLowerCase().includes(query.toLowerCase()))
    : items;
  return (
    <Flex vertical gap="sm" className={styles.overflowPopover}>
      <Flex align="center" gap="xs" className={styles.overflowSearch}>
        <Icon name="search" size={12} weight={iconWeight.light} className={styles.overflowSearchIcon} />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          className={styles.overflowSearchInput}
        />
      </Flex>
      <div className={styles.overflowList}>
        {filtered.length > 0
          ? <Tag className={className}>{filtered}</Tag>
          : <Text className={styles.overflowEmpty}>No results</Text>
        }
      </div>
    </Flex>
  );
}

function TruncatedList({ Tag, className, children }: TruncatedListProps) {
  // Filter out whitespace-only text nodes react-markdown injects between <li>s
  const allItems = children.filter((c) => !(typeof c === 'string' && c.trim() === ''));
  const overflowItems = allItems.slice(LIST_MAX);

  if (overflowItems.length === 0) {
    return <Tag className={className}>{allItems}</Tag>;
  }

  const visibleItems = allItems.slice(0, LIST_MAX);

  return (
    <div className={styles.truncatedListWrapper}>
      <Tag className={className}>{visibleItems}</Tag>
      <Popover
        trigger="click"
        placement="top-start"
        role="dialog"
        arrow={false}
        content={<OverflowContent items={overflowItems} Tag={Tag} className={className} />}
      >
        <span>
          <Button variant="text" className={styles.overflowBtn} aria-label={`+${overflowItems.length} more`}>
            +{overflowItems.length} more
          </Button>
        </span>
      </Popover>
    </div>
  );
}

const NULA_LINK_RE = /^nula:\/\/(\w+)\/(.+)$/;

interface MarkdownRendererProps {
  children: string;
  className?: string;
}

/**
 * Renders markdown text using react-markdown + remark-gfm.
 * Intercepts `nula://document/` and `nula://collection/` links to route them
 * through the shared Link component (hover preview card + in-app navigation).
 * External links open in a new tab.
 */
export function MarkdownRenderer({ children, className }: MarkdownRendererProps) {
  const navigateToEntity = useNavigateToEntity();
  const selectEventById = useCalendarStore((s) => s.selectEventById);

  const components: Components = React.useMemo(
    () => ({
      a({ href, children: linkChildren }) {
        if (!href) return <span>{linkChildren}</span>;
        const nulaMatch = NULA_LINK_RE.exec(href);
        if (nulaMatch) {
          const [, entityType, entityId] = nulaMatch;
          if (entityType === 'document') {
            return <Link href={href}>{linkChildren}</Link>;
          }
          if (entityType === 'collection') {
            return (
              <Link href={href} onNavigate={() => navigateToEntity(entityId)}>
                {linkChildren}
              </Link>
            );
          }
          if (entityType === 'event') {
            return (
              <Link href={href} onNavigate={() => selectEventById(entityId)}>
                {linkChildren}
              </Link>
            );
          }
          // Unknown nula:// entity type — render as plain text
          return <span>{linkChildren}</span>;
        }
        // External link
        return <Link href={href}>{linkChildren}</Link>;
      },
      // Prevent react-markdown from wrapping everything in <p> when the text
      // is a single inline line — avoids unwanted block margin on short strings.
      p({ children: pChildren }) {
        return <span className={styles.p}>{pChildren}</span>;
      },
      h1: ({ children: c }) => <h1 className={styles.h1}>{c}</h1>,
      h2: ({ children: c }) => <h2 className={styles.h2}>{c}</h2>,
      h3: ({ children: c }) => <h3 className={styles.h3}>{c}</h3>,
      h4: ({ children: c }) => <h4 className={styles.h4}>{c}</h4>,
      h5: ({ children: c }) => <h5 className={styles.h5}>{c}</h5>,
      h6: ({ children: c }) => <h6 className={styles.h6}>{c}</h6>,
      ul: ({ children: c }) => (
        <TruncatedList Tag="ul" className={styles.ul}>{React.Children.toArray(c)}</TruncatedList>
      ),
      ol: ({ children: c }) => (
        <TruncatedList Tag="ol" className={styles.ol}>{React.Children.toArray(c)}</TruncatedList>
      ),
      li: ({ children: c }) => <li className={styles.li}>{c}</li>,
      code: ({ children: c, className: codeClassName }) => {
        const isBlock = codeClassName?.startsWith('language-');
        return isBlock
          ? <code className={styles.codeBlock}>{c}</code>
          : <code className={styles.code}>{c}</code>;
      },
      pre: ({ children: c }) => <pre className={styles.pre}>{c}</pre>,
      table: ({ children: c }) => <table className={styles.table}>{c}</table>,
      thead: ({ children: c }) => <thead className={styles.thead}>{c}</thead>,
      tbody: ({ children: c }) => <tbody>{c}</tbody>,
      tr: ({ children: c }) => <tr className={styles.tr}>{c}</tr>,
      th: ({ children: c }) => <th className={styles.th}>{c}</th>,
      td: ({ children: c }) => <td className={styles.td}>{c}</td>,
      blockquote: ({ children: c }) => <blockquote className={styles.blockquote}>{c}</blockquote>,
      strong: ({ children: c }) => <strong className={styles.strong}>{c}</strong>,
      em: ({ children: c }) => <em className={styles.em}>{c}</em>,
      hr: () => <hr className={styles.hr} />,
    }),
    [navigateToEntity, selectEventById],
  );

  return (
    <div className={`${styles.root}${className ? ` ${className}` : ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
        urlTransform={(url) => url}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
